<script>
import CustomArrow from '@components/custom-arrow'
import { mapState, mapGetters } from 'vuex'
import {
  zendeskActions,
  zendeskValidateActions,
  firebaseActions,
} from '@state/helpers'
import deepLink from '@/mixins/deeplink.js'
import kustomer from '@/mixins/kustomer.js'

import axios from 'axios'
import ENV from '@src/env'
import Raven from 'raven-js'

import GTM_CONSTANTS from '@constants/google-tag-manager'
import AMPLITUDE from '@constants/amplitude'
import MultipleAttentionModal from '@components/modals/multiple-attention-modal'

import { keysToCamel, getPropsFromObject } from '../utils/object-utils'

export default {
  name: 'MultipleAttentionChannels',
  components: {
    MultipleAttentionModal,
    CustomArrow,
  },
  mixins: [deepLink(), kustomer()],
  props: {
    componentData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      textareaContent: '',
      textAreaMinLength: 5,
      textAreaMaxLength: 150,
      ticketChannel: false,
      conversationId: '',
      disableChatButton: false,
      disableEmailButton: true,
    }
  },
  computed: {
    ...mapState('zendesk', {
      fetching: state => state.fetching,
    }),
    ...mapState('widgets', {
      user: state => state.data.SharedData.userData,
      common: state => state.common,
    }),
    ...mapGetters('widgets', ['userEnv', 'notSatisfiedUser']),
    ...mapGetters('zendesk', [
      'computedSubject',
      'validateSend',
      'computedTags',
      'formatedBody',
    ]),
    ...mapState('zendesk', {
      ticketsByOrder: state => state.ticketsByOrder,
    }),
    ticket() {
      return this.componentData.channels.ticket || {}
    },
    chat() {
      return this.componentData.channels.chat || {}
    },
    whatsApp() {
      return this.componentData.channels.whatsApp || {}
    },
    token() {
      return this.$route.query.token || {}
    },
    ticketTags() {
      return this.ticketsByOrder.tags || []
    },
    isOnlyChatChannel() {
      return this.ticketChannel === 'chat'
    },
    getTextForButton() {
      return this.isOnlyChatChannel
        ? this.$t('WIDGETS.MULTIPLE_ATTENTION_CHANNELS.ONLY_ONE_CHANNEL_CHAT')
        : this.$t('WIDGETS.MULTIPLE_ATTENTION_CHANNELS.ONLY_ONE_CHANNEL_TICKET')
    },
    userForLogs() {
      let { user } = this.common

      user = Object.assign({}, user, {
        userLogsId: this.logsUserID,
      })
      return user
    },
    textAreaHasMinLength() {
      return this.textareaContent.length > this.textAreaMinLength
    },
    textAreaHasMaxLimit() {
      return this.textareaContent.length <= this.textAreaMaxLength
    },
    textAreaLimits() {
      return this.textAreaHasMinLength && this.textAreaHasMaxLimit
    },
    textAreaIsEmpty() {
      return this.textareaContent.length === 0
    },
  },
  watch: {
    textareaContent() {
      if (
        this.textareaContent.length >= this.textAreaMaxLength ||
        this.textareaContent.length < 5
      ) {
        this.disableEmailButton = true
      }

      this.commitZendeskCommon({
        key: 'comment',
        data: {
          body: this.textareaContent,
        },
      })

      if (this.textAreaHasMinLength && this.textAreaHasMaxLimit) {
        this.commitValidatedField({
          type: 'function',
          key: 'commentValidation',
          name,
          value: this.textareaContent,
        })
      }
    },
    validateSend() {
      if (this.validateSend.length === 0 && this.textareaContent.length !== 0) {
        this.disableEmailButton = false
      }
    },
  },
  async created() {
    await this.getFirebaseDatabaseURLS()
    // fix start condition according widget state
    if (this.chat.show) await this.startChat()

    this.textareaContent = this.comment ? this.comment.body : ''
    this.validateIfRequired()
    let callSecondEndpint = await this.callEndpointWithNoOrderId()
    if (callSecondEndpint) {
      const data = await this.getTicketStatus()
      if (data) {
        this.ticketChannel = data.channel
      }

    }
  },
  methods: {
    ...zendeskActions,
    ...zendeskValidateActions,
    ...firebaseActions,
    validateIfRequired() {
      this.commitRequiredFields({
        type: 'function',
        key: 'commentValidation',
        name,
      })
    },
    checkIfOrderIdNotExits() {
      const query = this.$route.query
      return query.order_id === '' || query.order_id === '0' || !query.order_id
    },
    async callEndpointWithNoOrderId() {
      if (this.checkIfOrderIdNotExits()) {
        this.validateWitourOrderId()
        return false
      }
      return true
    },
    async validateWitourOrderId() {
      const timResponse = await this.getTicketsWithourOrderId()
      if (timResponse) {
        const { channel, ticketId } = timResponse
        this.ticketChannel = channel
        this.conversationId = ticketId
      }
    },
    useCreateTicket() {
      if (this.validateSend.length === 0 && this.textAreaHasMinLength) {
        this.logEvent(AMPLITUDE.CREATED_EMAIL)
        this.createTicket({
          params: {
            provider: this.ticket.crm.toLowerCase(),
          },
        })
      }
    },
    createKustomerUser() {
      return axios.post(
        `${
          ENV.rappi.url[this.userEnv]
        }/api/ticket-manager/tickets/create-kustomer-user`,
        {},
        {
          headers: { Authorization: `Bearer ${this.token}` },
        }
      )
    },
    async redirectToWhatsapp(deepLink) {
      try {
        await this.createKustomerUser()
        this.logEvent(AMPLITUDE.CREATED_WHATSAPP)
      } catch (err) {
        Raven.captureException(err)
      } finally {
        this.thirdPartyDeepLink(deepLink)
      }
    },
    determinateChannel() {
      if (this.isOnlyChatChannel) {
        this.redirectToChat()
      } else {
        this.redirectToOrderHistory()
      }
    },
    redirectToChat() {
      const { deeplinkParams } = this.chat.data
      this.callDeepLink(deeplinkParams)
    },
    async redirectToOrderHistory() {
      const checkIfOrder = await this.callEndpointWithNoOrderId()
      if (checkIfOrder){
        const { ticketId } = await this.getTicketStatus()
        this.conversationId = ticketId
      }
      this.callDeepLink(
        `goto=help_center&conversation_id=${this.conversationId}`
      )
    },
    async beginChat() {
      const { tag } = this.$route.query
      const chatKey = tag ? `&chat_key=${tag}` : ''
      const deeplink = `${this.chat.data.deeplinkParams}${chatKey}`
      this.logEvent(AMPLITUDE.CREATED_CHAT)
      this.callDeepLink(deeplink)
    },
    logEvent(eventType) {
      const { articleId } = this.$route.params
      // eslint-disable-next-line camelcase
      const logKeys = [
        'country_code',
        'dev',
        'language',
        'order_id',
        'payment_amount',
        'payment_id',
      ]
      const propsFromQuery = keysToCamel(
        getPropsFromObject(this.$route.query, logKeys)
      )

      const payload = {
        articleId,
        ...propsFromQuery,
      }

      this.$gtm.trackEvent({
        payload,
        eventType,
        action: eventType,
        category: 'CONVERSATION_CREATED',
        event: GTM_CONSTANTS.LOG_EVENT,
        noninteraction: false,
        user: this.userForLogs,
      })
    },
  },
}
</script>

<template>
  <section
    v-if="notSatisfiedUser"
    :class="$style.container"
    :id="`${this.$options.name}-container`"
  >
    <div v-if="!ticketChannel">
      <div
        v-if="!ticket.chat"
        :class="$style.container__help">
        <h3>{{ $t('WIDGETS.MULTIPLE_ATTENTION_CHANNELS.TITLE') }}</h3>
      </div>
      <div :class="$style.container__omnichannel">
        <div
          v-if="!ticket.chat "
          :class="$style.container__omnichannel__input">
          <v-textarea
            v-model="textareaContent"
            :label="ticket.copies.placeholder"
            :auto-grow="true"
            prepend-inner-icon="insert_comment"
            counter="150"
            rows="1"
          />
        </div>
        <div :class="$style.container__omnichannel__contact">
          <div
            v-if="ticket.show"
            :class="$style.container__omnichannel__contact__email"
          >
            <BaseButton
              :id="`${this.$options.name}-actions-email`"
              :disabled="disableEmailButton"
              full
              @click="useCreateTicket"
            >
              {{ $t('WIDGETS.MULTIPLE_ATTENTION_CHANNELS.EMAIL') }}
            </BaseButton>
          </div>
          <div
            v-if="chat.show"
            :class="$style.container__omnichannel__contact__chat"
            @click="beginChat"
          >
            <p>
              {{ $t('WIDGETS.MULTIPLE_ATTENTION_CHANNELS.CHAT') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="ticketChannel"
      @click="determinateChannel">
      <BaseButton>
        {{ getTextForButton }}
      </BaseButton>
    </div>
  </section>
</template>

<style scoped lang="scss" module>
@import '@design';

.container {
  padding: 1em;
  background: $background-container !important;

  &__help {
    display: flex;
    flex-flow: column wrap;
    align-content: flex-start;
    text-align: left;

    h3 {
      color: $color-heading-text;
    }

    h4 {
      color: $secondary-text;
      font-weight: semibold;
    }
  }

  &__omnichannel {
    display: flex;
    flex-flow: column wrap;
    align-items: flex-start;
    margin-top: 1.3em;
    width: 100%;
    padding: 1em 0;

    &__input {
      width: 100%;
    }

    &__contact {
      display: flex;
      flex-flow: column wrap;
      width: 100%;
      margin-top: 1em;

      &__email {
        width: 100%;
      }

      &__chat {
        font-weight: bold;
        color: $rappi-green;
        font-size: 1.2em;
        margin-top: 1.5em;
        cursor: pointer;
      }
    }
  }
}
</style>
